import { useMutation, useQuery } from 'react-query';
import {
  apiGetList,
  apiGetById,
  apiPut,
  apiDelete,
} from '../services/apiClient';

/**
 * Contact-us messages sent from the public website's /academics/contact-us page.
 *
 * Every call goes through `services/apiClient`, which applies the Backend API
 * Contract in one place — the envelope assertion, the `error`-over-`message`
 * precedence, and the `{ items, totalCount, currentPage, pageSize }` list shape.
 * See DPA-FE `docs/APICall/BackendAPIContract.md`.
 *
 * The ALLOWED-RECIPIENT list (`/api/ContactUs/Settings`) used to live at the
 * bottom of this file against two endpoints that did not exist. The backend has
 * since shipped a full CRUD for it, and it now has a screen of its own — see
 * `Hooks/ContactSettings.js` and `Template/ContactSettingsTemplate`.
 *
 * Backend routes, re-verified against the live API 2026-09-22:
 *
 *   GET    /api/ContactUs/{page}/{pageSize}  list   -> data.items[], totalCount
 *   GET    /api/ContactUs/{id}               one    -> data (the entity)
 *   PUT    /api/ContactUs/{id}/Read          mark read
 *   DELETE /api/ContactUs/{id}               delete
 *
 * THE LIST ROUTE IS NOW DEPLOYED. It 404'd when this file was written
 * (2026-09-16) and the note here said so; that is out of date and the screen no
 * longer needs to explain a missing route.
 *
 * PAGE IS ONE-BASED — the first page is `/1/10`. Measured: `/1/2` returns the
 * first two rows, `/2/2` the next, and `/0/2` answers HTTP 500. That is the
 * opposite of the signed contract (§5.1 says zero-based); the deviation is
 * deliberate, comes from the backend team, and is recorded in
 * `DPA-FE/docs/APICall/DPA-CONFORMANCE.md`. `pageSize: 0` means "return all".
 *
 * MULTI-COLUMN FILTERING — the wire format CONFIRMED BY THE BACKEND TEAM on
 * 2026-09-23 is ASP.NET indexed complex binding, one entry per criterion,
 * AND semantics across entries:
 *
 *   ?Filters[0].FilterBy=FullName&Filters[0].FilterValue=umair
 *   &Filters[1].FilterBy=IsRead&Filters[1].FilterValue=false
 *
 * Notes on that shape:
 *
 * - The parameter names and the FIELD NAMES are PascalCase in the backend's
 *   own example (`FullName`, `IsRead`, `ReceiverEmail`) — they are C# property
 *   names. The UI's camelCase field ids are translated through
 *   `WIRE_FIELD_NAMES` below; send exactly what the backend documented.
 * - `URLSearchParams` percent-encodes the brackets (`Filters%5B0%5D.FilterBy`);
 *   ASP.NET decodes before binding, so that is equivalent and correct.
 * - ONE pair also travels as `Filters[0].…` — one code path for every count,
 *   and no dependency on whether the legacy `filterBy`/`filterValue` pair
 *   survived the backend change.
 *
 * ⚠ FILTERING IS NOT FORGIVING — before this change, an unsupported field or a
 * value the field cannot parse answered HTTP 500, not an empty list. Measured
 * (2026-09-22, legacy single-pair form):
 *
 *   fullName · email · mobile · subject · message · receiverEmail
 *                                     -> substring match, any text
 *   isRead · isEmailSentRequired      -> `true`/`false` ONLY; `1` is a 500
 *   referenceNumber                   -> an integer ONLY; `abc` is a 500
 *   createdOn · id · anything else    -> ALWAYS a 500, unsupported
 *
 * Until the Filters[] deployment is re-measured, assume the same rules apply
 * per entry. So the field list stays CLOSED in the UI, and a field and its
 * value travel together — both enforced in `ContactMessagesFilters`, which only
 * emits committed pairs. Free dates go through `fromDate`/`toDate` instead,
 * which are inclusive on both ends.
 */

const RESOURCE = 'ContactUs';

/** UI field id -> the C# property name the Filters[] binder expects. */
const WIRE_FIELD_NAMES = {
  referenceNumber: 'ReferenceNumber',
  fullName: 'FullName',
  email: 'Email',
  mobile: 'Mobile',
  subject: 'Subject',
  message: 'Message',
  receiverEmail: 'ReceiverEmail',
  isRead: 'IsRead',
};

/**
 * @param {object}  opts
 * @param {number}  opts.page         1-based (see the note above)
 * @param {number}  opts.pageSize     0 = all
 * @param {string}  opts.searchText   free text across every sensible field
 * @param {string}  opts.fromDate     `YYYY-MM-DD`, inclusive
 * @param {string}  opts.toDate      `YYYY-MM-DD`, inclusive
 * @param {Array<{field: string, value: string}>} opts.filters
 *        column criteria, ANDed by the server; [] filters nothing
 *
 * Every filter is optional and empty ones are dropped before the URL is built,
 * so an untouched screen sends a clean `/1/10` with no stray `?Filters[…]`.
 */
export const useFetchContactMessages = ({
  page = 1,
  pageSize = 0,
  searchText = '',
  fromDate = '',
  toDate = '',
  filters = [],
} = {}) => {
  // The backend-confirmed indexed shape (see the header note). Indices are
  // dense and start at 0 regardless of which chips the operator removed, so
  // the binder never sees a gap.
  const filterParams = {};
  filters.forEach((criterion, index) => {
    if (!criterion?.field || !criterion?.value) return;
    filterParams[`Filters[${index}].FilterBy`] =
      WIRE_FIELD_NAMES[criterion.field] ?? criterion.field;
    filterParams[`Filters[${index}].FilterValue`] = criterion.value;
  });

  return useQuery({
    // Every filter is part of the key: two different filter sets are two
    // different results and must not share a cache entry.
    queryKey: [
      'contactMessages',
      page,
      pageSize,
      searchText,
      fromDate,
      toDate,
      filters,
    ],
    queryFn: () =>
      apiGetList(RESOURCE, {
        page,
        pageSize,
        searchText,
        fromDate,
        toDate,
        ...filterParams,
      }),
    // Paging must not blank the table while the next page loads.
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

export const useFetchContactMessageById = (id) =>
  useQuery({
    queryKey: ['contactMessage', id],
    queryFn: () => apiGetById(RESOURCE, id),
    enabled: false,
    refetchOnWindowFocus: false,
  });

/** Action endpoint, contract §5.8: noun-then-verb on the resource path. */
export const useMarkContactMessageRead = () =>
  useMutation((id) => apiPut(`${RESOURCE}/${id}/read`));

export const useDeleteContactMessage = () =>
  useMutation((id) => apiDelete(RESOURCE, id));
